import { useEffect, useRef, useState } from 'react';

/**
 * GOOGLE AGENDA, ESSAI LOCAL de la porte Ma semaine.
 *
 * CE QUE C'EST : une LECTURE SEULE de l'agenda principal de la personne qui se
 * connecte, pour voir ses evenements Google a cote des creneaux Taki. Rien
 * n'est ecrit chez Google, rien n'est ecrit chez Taki.
 *
 * COMMENT : Google Identity Services, charge a la volee depuis Google (aucune
 * dependance npm), delivre un jeton d'acces d'une heure, garde EN MEMOIRE
 * seulement : recharger la page deconnecte. L'API Calendar accepte l'appel
 * direct depuis le navigateur avec ce jeton.
 *
 * LE SEUL REGLAGE est l'ID client OAuth, public par nature, lu dans
 * `VITE_GOOGLE_CLIENT_ID` (`apps/hello/.env.local`, ignore par git). Sans lui,
 * la porte ne montre rien de Google. Le CODE SECRET du client ne sert pas ici
 * et n'entre jamais dans le depot.
 *
 * CE QUE CE N'EST PAS : une synchronisation. Une synchro durable (jeton de
 * rafraichissement, ecriture chez Google) demande un secret, donc une Function
 * du socle (`docs/FUNCTIONS.md`) : c'est un chantier `core/`, pas cette porte.
 */

export const GOOGLE_CLIENT_ID: string =
  (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() ?? '';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
/** Lire les evenements, rien d'autre : le droit le plus etroit qui suffit. */
const SCOPE = 'https://www.googleapis.com/auth/calendar.events.readonly';
const EVENTS_URL = 'https://www.googleapis.com/calendar/v3/calendars/primary/events';

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
}
interface TokenClient {
  requestAccessToken: () => void;
}
interface GoogleOauth2 {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: TokenResponse) => void;
    error_callback?: (error: { type?: string }) => void;
  }) => TokenClient;
  revoke: (token: string, done?: () => void) => void;
}
type GoogleWindow = Window & { google?: { accounts?: { oauth2?: GoogleOauth2 } } };

const oauth2 = (): GoogleOauth2 | undefined => (window as GoogleWindow).google?.accounts?.oauth2;

let loading: Promise<GoogleOauth2> | null = null;
function loadGoogleIdentity(): Promise<GoogleOauth2> {
  const ready = oauth2();
  if (ready != null) return Promise.resolve(ready);
  loading ??= new Promise<GoogleOauth2>((resolve, reject) => {
    const fail = (message: string): void => {
      loading = null;
      reject(new Error(message));
    };
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => {
      const api = oauth2();
      if (api == null) fail('Google Identity Services introuvable');
      else resolve(api);
    };
    script.onerror = () => fail('Script Google injoignable');
    document.head.append(script);
  });
  return loading;
}

/** Un evenement Google pose sur la grille : jour 0 = lundi, heures decimales. */
export interface GoogleWeekEvent {
  id: string;
  title: string;
  day: number;
  start: number;
  duration: number;
  link: string | null;
}

/**
 * Un evenement de la rangee JOURNEE ENTIERE : jours 0 (lundi) a 6 (dimanche),
 * bornes INCLUSES et deja coupees a la semaine affichee.
 */
export interface GoogleAllDayEvent {
  id: string;
  title: string;
  firstDay: number;
  lastDay: number;
  link: string | null;
}

interface WeekEvents {
  timed: GoogleWeekEvent[];
  allDay: GoogleAllDayEvent[];
}

interface RawEvent {
  id?: string;
  status?: string;
  summary?: string;
  htmlLink?: string;
  start?: { dateTime?: string; date?: string };
  end?: { dateTime?: string; date?: string };
}

class GoogleSessionExpired extends Error {}

const DAY_MS = 86_400_000;

/** Le numero du jour dans la semaine, 0 = lundi. Arrondi : un jour de changement d'heure dure 23 ou 25 heures. */
function dayIndex(date: Date, monday: Date): number {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((day.getTime() - monday.getTime()) / DAY_MS);
}
/** Une date pure `YYYY-MM-DD`, lue comme un jour LOCAL, jamais comme minuit UTC. */
function localDay(key: string): Date {
  const [year = 1970, month = 1, day = 1] = key.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Un evenement Google vers la semaine. Trois cas :
 * - JOURNEE ENTIERE (dates pures, fin EXCLUSIVE : un evenement du seul lundi
 *   finit le mardi) : rangee des journees entieres ;
 * - evenement horaire de 24 HEURES OU PLUS : meme rangee, comme dans Google
 *   Agenda, sinon il serait coupe a minuit le premier jour ;
 * - le reste : la grille horaire, coupe a minuit s'il deborde. Un evenement
 *   horaire commence avant le lundi est ecarte : c'est un essai, pas un moteur
 *   d'agenda.
 */
function placeEvent(item: RawEvent, monday: Date, into: WeekEvents): void {
  if (item.status === 'cancelled' || item.id == null) return;
  const summary = item.summary?.trim();
  const title = summary != null && summary !== '' ? summary : 'Sans titre';
  const link = item.htmlLink ?? null;
  const pushAllDay = (first: number, last: number): void => {
    if (last < 0 || first > 6 || last < first) return;
    into.allDay.push({ id: item.id ?? '', title, link, firstDay: Math.max(0, first), lastDay: Math.min(6, last) });
  };
  if (item.start?.date != null && item.end?.date != null) {
    pushAllDay(dayIndex(localDay(item.start.date), monday), dayIndex(localDay(item.end.date), monday) - 1);
    return;
  }
  const startIso = item.start?.dateTime;
  const endIso = item.end?.dateTime;
  if (startIso == null || endIso == null) return;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (end.getTime() - start.getTime() >= DAY_MS) {
    const endsAtMidnight = end.getHours() === 0 && end.getMinutes() === 0;
    pushAllDay(dayIndex(start, monday), dayIndex(end, monday) - (endsAtMidnight ? 1 : 0));
    return;
  }
  const day = dayIndex(start, monday);
  if (day < 0 || day > 6) return;
  const startHour = start.getHours() + start.getMinutes() / 60;
  const endHour = Math.min(24, startHour + (end.getTime() - start.getTime()) / 3_600_000);
  into.timed.push({ id: item.id, title, day, start: startHour, duration: Math.max(1 / 12, endHour - startHour), link });
}

/**
 * Les deux refus attendus sur un projet Google neuf, dits en clair plutot
 * qu'en code HTTP : l'API Calendar pas encore activee dans le projet, ou la
 * case de l'agenda decochee dans la fenetre de consentement.
 */
async function describeFailure(response: Response): Promise<string> {
  type Reason = { reason?: string };
  const body = (await response.json().catch(() => null)) as { error?: { errors?: Reason[]; details?: Reason[] } } | null;
  const reasons = [...(body?.error?.errors ?? []), ...(body?.error?.details ?? [])].map((item) => item.reason);
  if (reasons.includes('accessNotConfigured') || reasons.includes('SERVICE_DISABLED')) {
    return 'Google Calendar API non activée dans le projet Google Cloud';
  }
  if (reasons.includes('insufficientPermissions') || reasons.includes('ACCESS_TOKEN_SCOPE_INSUFFICIENT')) {
    return 'Lecture de l’agenda non autorisée dans la fenêtre Google';
  }
  return `Google Agenda a répondu ${response.status}`;
}

async function fetchWeekEvents(token: string, monday: Date, signal: AbortSignal): Promise<WeekEvents> {
  const end = new Date(monday);
  end.setDate(end.getDate() + 7);
  const params = new URLSearchParams({
    timeMin: monday.toISOString(),
    timeMax: end.toISOString(),
    singleEvents: 'true',
    orderBy: 'startTime',
    maxResults: '250',
  });
  const response = await fetch(`${EVENTS_URL}?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  if (response.status === 401) throw new GoogleSessionExpired();
  if (!response.ok) throw new Error(await describeFailure(response));
  const body = (await response.json()) as { items?: RawEvent[] };
  const week: WeekEvents = { timed: [], allDay: [] };
  for (const item of body.items ?? []) placeEvent(item, monday, week);
  return week;
}

const POPUP_ERRORS: Record<string, string> = {
  popup_closed: 'Connexion Google annulée',
  popup_failed_to_open: 'Fenêtre Google bloquée par le navigateur',
};

export interface GoogleAgenda {
  /** Un ID client est configure : sinon, rien de Google ne s'affiche. */
  available: boolean;
  connected: boolean;
  /** Connexion ou lecture de la semaine en cours. */
  busy: boolean;
  error: string | null;
  /** Les evenements horaires de la semaine affichee, vides tant qu'on n'est pas connecte. */
  events: readonly GoogleWeekEvent[];
  /** Les journees entieres de la semaine affichee, meme regle. */
  allDay: readonly GoogleAllDayEvent[];
  connect: () => void;
  disconnect: () => void;
}

export function useGoogleAgenda(monday: Date): GoogleAgenda {
  const available = GOOGLE_CLIENT_ID !== '';
  const requestToken = useRef<(() => void) | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventsByWeek, setEventsByWeek] = useState<Record<string, WeekEvents>>({});
  const weekId = String(monday.getTime());

  // Le script se charge DES L'OUVERTURE de la porte, pas au clic : la fenetre
  // Google doit s'ouvrir dans le meme geste que le clic, sinon le navigateur la
  // bloque comme une fenetre surgissante.
  useEffect(() => {
    if (!available) return undefined;
    let alive = true;
    loadGoogleIdentity()
      .then((api) => {
        if (!alive) return;
        const client = api.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: SCOPE,
          callback: (response) => {
            setConnecting(false);
            if (response.error != null || response.access_token == null) {
              setError(response.error === 'access_denied' ? 'Accès à Google Agenda refusé' : 'Connexion Google impossible');
              return;
            }
            setError(null);
            setToken(response.access_token);
          },
          error_callback: (failure) => {
            setConnecting(false);
            setError(POPUP_ERRORS[failure.type ?? ''] ?? 'Connexion Google impossible');
          },
        });
        requestToken.current = () => client.requestAccessToken();
      })
      .catch((failure: unknown) => {
        if (alive) setError(failure instanceof Error ? failure.message : 'Google indisponible');
      });
    return () => {
      alive = false;
    };
  }, [available]);

  useEffect(() => {
    if (token == null) return undefined;
    const controller = new AbortController();
    setReading(true);
    fetchWeekEvents(token, new Date(Number(weekId)), controller.signal)
      .then((events) => {
        setEventsByWeek((current) => ({ ...current, [weekId]: events }));
        setError(null);
      })
      .catch((failure: unknown) => {
        if (controller.signal.aborted) return;
        if (failure instanceof GoogleSessionExpired) {
          setToken(null);
          setEventsByWeek({});
          setError('Session Google expirée');
          return;
        }
        setError(failure instanceof Error ? failure.message : 'Lecture de Google Agenda impossible');
      })
      .finally(() => {
        if (!controller.signal.aborted) setReading(false);
      });
    return () => controller.abort();
  }, [token, weekId]);

  const connect = (): void => {
    if (requestToken.current == null) {
      setError('Google n’est pas encore prêt');
      return;
    }
    setError(null);
    setConnecting(true);
    requestToken.current();
  };
  const disconnect = (): void => {
    if (token != null) oauth2()?.revoke(token);
    setToken(null);
    setEventsByWeek({});
    setReading(false);
    setError(null);
  };

  return {
    available,
    connected: token != null,
    busy: connecting || reading,
    error,
    events: token == null ? [] : eventsByWeek[weekId]?.timed ?? [],
    allDay: token == null ? [] : eventsByWeek[weekId]?.allDay ?? [],
    connect,
    disconnect,
  };
}
